import {Accordion, AccordionDetails, AccordionSummary, Box, LinearProgress, Stack, Typography} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {DatePicker} from "@mui/x-date-pickers"
import dayjs, {Dayjs} from "dayjs"
import useApiCall from "../../hooks/CancellableApiCall"
import {ObservationReportSummaryResponse} from "../../data/ObservationReportSummaryResponse"
import {LoadingContent} from "../LoadingContent"
import {useState} from "react"

interface ObservationReportSummaryProps {
    communityId: string
}

const buildUrl = (communityId: string, from: Dayjs, to: Dayjs) =>
    `/api/observation-report/user/${communityId}/summary?from=${from.startOf("day").toISOString()}&to=${to.endOf("day").toISOString()}`

export const ObservationReportSummary = (props: ObservationReportSummaryProps) => {
    const [from, setFrom] = useState<Dayjs>(dayjs().subtract(30, "day"))
    const [to, setTo] = useState<Dayjs>(dayjs())

    const summary = useApiCall<ObservationReportSummaryResponse>({
        initialUrl: buildUrl(props.communityId, from, to)
    })

    const handleFromChange = (v: Dayjs | null) => {
        if (v && v.isValid()) {
            setFrom(v)
            summary.setUrl(buildUrl(props.communityId, v, to))
        }
    }

    const handleToChange = (v: Dayjs | null) => {
        if (v && v.isValid()) {
            setTo(v)
            summary.setUrl(buildUrl(props.communityId, from, v))
        }
    }

    const d = summary.data

    const scoreRow = (label: string, score: number, max: number) => (
        <Stack direction="row" justifyContent="space-between" key={label}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2">{score} / {max}</Typography>
        </Stack>
    )

    return (
        <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="subtitle1">Score Summary</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack direction="row" spacing={2} sx={{mb: 2}}>
                    <DatePicker label="From" value={from} onChange={handleFromChange}
                                slotProps={{textField: {variant: "standard", size: "small"}}}/>
                    <DatePicker label="To" value={to} onChange={handleToChange}
                                slotProps={{textField: {variant: "standard", size: "small"}}}/>
                </Stack>
                <LoadingContent isLoading={summary.isLoading} showLoadingSkeleton>
                    {d && (
                        <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                {d.reportCount} report{d.reportCount !== 1 ? "s" : ""} in period
                            </Typography>
                            {scoreRow("Leadership", d.leadershipScore, d.leadershipMax)}
                            {scoreRow("Communication", d.communicationScore, d.communicationMax)}
                            {scoreRow("Investigative Skills", d.investigativeScore, d.investigativeMax)}
                            {scoreRow("Law / Policy Knowledge", d.knowledgeScore, d.knowledgeMax)}
                            {scoreRow("Rank Performance", d.rankScore, d.rankMax)}
                            <Box sx={{pt: 1}}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2"><strong>Total</strong></Typography>
                                    <Typography variant="body2"><strong>{d.totalScore} / {d.maxScore}</strong></Typography>
                                </Stack>
                                <LinearProgress variant="determinate" color="secondary"
                                                value={d.maxScore > 0 ? (d.totalScore / d.maxScore) * 100 : 0}
                                                sx={{mt: 0.5}}/>
                            </Box>
                        </Stack>
                    )}
                </LoadingContent>
            </AccordionDetails>
        </Accordion>
    )
}
